import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  CloudUpload,
  Add,
  Edit,
  Delete,
  Sync,
  Folder,
  InsertDriveFile,
  NavigateNext,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Info,
  OpenInNew,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function DropboxIntegration() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncs, setSyncs] = useState([]);
  const [stations, setStations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [editingSync, setEditingSync] = useState(null);
  const [syncFormData, setSyncFormData] = useState({
    name: '',
    station_id: '',
    dropbox_path: '',
    local_category_id: '',
    auto_import: true,
    auto_overwrite: true,
    enabled: true,
    sync_schedule: '',
    sync_interval_minutes: '',
    sync_on_change: false,
  });
  const [scheduleType, setScheduleType] = useState('manual'); // 'manual', 'interval', 'cron'
  const [dropboxPath, setDropboxPath] = useState('');
  const [dropboxFiles, setDropboxFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedSyncId, setSelectedSyncId] = useState(null);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [dropboxConfig, setDropboxConfig] = useState({
    appKey: '',
    appSecret: '',
    redirectUri: '',
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [needsConfiguration, setNeedsConfiguration] = useState(false);

  useEffect(() => {
    checkConnection();
    fetchSyncs();
    fetchStations();
    fetchCategories();
    if (isAdmin) {
      fetchDropboxConfig();
    }
  }, [isAdmin]);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dropbox/auth/status`, {
        withCredentials: true,
      });
      setConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
      setConnected(false);
    }
  };

  const fetchSyncs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dropbox/syncs`, {
        withCredentials: true,
      });
      setSyncs(response.data);
    } catch (error) {
      console.error('Error fetching syncs:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true,
      });
      setStations(response.data || []);
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchCategories = async (stationId = null) => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`, {
        params: stationId ? { station_id: stationId } : {},
        withCredentials: true,
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    if (!syncDialogOpen) return;
    fetchCategories(syncFormData.station_id || null);
  }, [syncDialogOpen, syncFormData.station_id]);

  const fetchDropboxConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dropbox/config`, {
        withCredentials: true,
      });
      setDropboxConfig({
        appKey: response.data.appKey || '',
        appSecret: '', // Never show secret
        redirectUri: response.data.redirectUri || `${API_URL}/api/dropbox/auth/callback`,
      });
      setNeedsConfiguration(!response.data.configured);
    } catch (error) {
      console.error('Error fetching Dropbox config:', error);
      if (error.response?.status === 403) {
        // Not admin, ignore
        return;
      }
      setNeedsConfiguration(true);
    }
  };

  const handleSaveConfig = async () => {
    if (!dropboxConfig.appKey || !dropboxConfig.appSecret) {
      alert('App Key and App Secret are required');
      return;
    }

    setConfigLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/dropbox/config`,
        dropboxConfig,
        { withCredentials: true }
      );
      setConfigDialogOpen(false);
      setNeedsConfiguration(false);
      alert('Dropbox credentials saved successfully');
      // Clear secret from state for security
      setDropboxConfig({ ...dropboxConfig, appSecret: '' });
    } catch (error) {
      console.error('Error saving config:', error);
      alert(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dropbox/auth/url`, {
        withCredentials: true,
      });
      if (response.data.needsConfiguration) {
        setNeedsConfiguration(true);
        alert('Dropbox app credentials not configured. Please configure them first.');
        if (isAdmin) {
          setConfigDialogOpen(true);
        }
        return;
      }
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      if (error.response?.data?.needsConfiguration) {
        setNeedsConfiguration(true);
        alert('Dropbox app credentials not configured. Please configure them first.');
        if (isAdmin) {
          setConfigDialogOpen(true);
        }
      } else {
        alert(error.response?.data?.message || 'Failed to connect to Dropbox');
      }
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Dropbox?')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/api/dropbox/auth/disconnect`, {}, {
        withCredentials: true,
      });
      setConnected(false);
      alert('Dropbox disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Failed to disconnect Dropbox');
    }
  };

  const handleOpenSyncDialog = (sync = null) => {
    if (sync) {
      setEditingSync(sync);
      const hasInterval = sync.sync_interval_minutes && sync.sync_interval_minutes > 0;
      const hasCron = sync.sync_schedule && sync.sync_schedule.length > 0;
      setScheduleType(hasInterval ? 'interval' : (hasCron ? 'cron' : 'manual'));
      setSyncFormData({
        name: sync.name,
        station_id: sync.station_id,
        dropbox_path: sync.dropbox_path,
        local_category_id: sync.local_category_id || '',
        auto_import: sync.auto_import,
        auto_overwrite: sync.auto_overwrite,
        enabled: sync.enabled,
        sync_schedule: sync.sync_schedule || '',
        sync_interval_minutes: sync.sync_interval_minutes || '',
        sync_on_change: sync.sync_on_change || false,
      });
      setDropboxPath(sync.dropbox_path);
    } else {
      setEditingSync(null);
      setScheduleType('manual');
      setSyncFormData({
        name: '',
        station_id: '',
        dropbox_path: '',
        local_category_id: '',
        auto_import: true,
        auto_overwrite: true,
        enabled: true,
        sync_schedule: '',
        sync_interval_minutes: '',
        sync_on_change: false,
      });
      setDropboxPath('');
    }
    setCurrentPath('');
    setDropboxFiles([]);
    setSyncDialogOpen(true);
  };

  const handleCloseSyncDialog = () => {
    setSyncDialogOpen(false);
    setEditingSync(null);
    setDropboxPath('');
    setCurrentPath('');
    setDropboxFiles([]);
  };

  const loadDropboxFiles = async (path = '') => {
    if (!connected) return;
    setLoadingFiles(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/dropbox/list`,
        { path },
        { withCredentials: true }
      );
      setDropboxFiles(response.data.entries);
      setCurrentPath(path);
    } catch (error) {
      console.error('Error loading Dropbox files:', error);
      alert('Failed to load Dropbox files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleSelectFolder = (path) => {
    setDropboxPath(path);
    setSyncFormData({ ...syncFormData, dropbox_path: path });
  };

  const handleSaveSync = async () => {
    if (!syncFormData.name || !syncFormData.station_id || !syncFormData.dropbox_path) {
      alert('Please fill in all required fields');
      return;
    }

    // Only the selected schedule type is saved. Switching type in the dropdown
    // leaves the other field's old value in the form, and an interval wins
    // over a cron expression on the server.
    const payload = {
      ...syncFormData,
      sync_interval_minutes: scheduleType === 'interval' ? syncFormData.sync_interval_minutes : '',
      sync_schedule: scheduleType === 'cron' ? syncFormData.sync_schedule : '',
    };

    setLoading(true);
    try {
      if (editingSync) {
        await axios.put(
          `${API_URL}/api/dropbox/syncs/${editingSync.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/dropbox/syncs`,
          payload,
          { withCredentials: true }
        );
      }
      handleCloseSyncDialog();
      fetchSyncs();
    } catch (error) {
      console.error('Error saving sync:', error);
      alert(error.response?.data?.message || 'Failed to save sync configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSync = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sync configuration?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/dropbox/syncs/${id}`, {
        withCredentials: true,
      });
      fetchSyncs();
    } catch (error) {
      console.error('Error deleting sync:', error);
      alert('Failed to delete sync');
    }
  };

  const handleSyncNow = async (id) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/dropbox/syncs/${id}/sync`,
        {},
        { withCredentials: true }
      );
      alert(response.data?.alreadyRunning
        ? 'This sync is already running. Check logs for progress.'
        : 'Sync started. Check logs for progress.');
      fetchSyncs();
    } catch (error) {
      console.error('Error triggering sync:', error);
      alert(error.response?.data?.message || 'Failed to start sync');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = async (id) => {
    setSelectedSyncId(id);
    try {
      const response = await axios.get(
        `${API_URL}/api/dropbox/syncs/${id}/logs`,
        { withCredentials: true }
      );
      setSyncLogs(response.data);
      setLogsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching logs:', error);
      alert('Failed to load logs');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCronExpression = (sync) => {
    if (sync.sync_interval_minutes && sync.sync_interval_minutes > 0) {
      const minutes = parseInt(sync.sync_interval_minutes);
      if (minutes >= 60) {
        return `Every ${Math.floor(minutes / 60)} hour(s)`;
      }
      return `Every ${minutes} minute(s)`;
    }
    if (sync.sync_schedule) {
      return sync.sync_schedule;
    }
    return 'Manual only';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#22d3ee' }}>
          Dropbox Integration
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!connected && (
            <>
              <IconButton
                onClick={() => setInstructionsDialogOpen(true)}
                sx={{ color: '#22d3ee' }}
                title="How to connect Dropbox"
              >
                <Info />
              </IconButton>
              {isAdmin && (
                <IconButton
                  onClick={() => setConfigDialogOpen(true)}
                  sx={{ color: '#22d3ee' }}
                  title="Configure Dropbox Credentials"
                >
                  <CloudUpload />
                </IconButton>
              )}
            </>
          )}
          {connected ? (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDisconnect}
              sx={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}
            >
              Disconnect Dropbox
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleConnect}
              sx={{ bgcolor: '#0061ff', '&:hover': { bgcolor: '#0052cc' } }}
            >
              Connect Dropbox
            </Button>
          )}
        </Box>
      </Box>

      {needsConfiguration && isAdmin && (
        <Alert severity="warning" sx={{ mb: 3, bgcolor: 'var(--rs-paper)', border: '1px solid #ff9800' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Dropbox app credentials need to be configured.</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please configure your Dropbox App Key and App Secret to enable Dropbox integration.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setConfigDialogOpen(true)}
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
          >
            Configure Dropbox Credentials
          </Button>
        </Alert>
      )}

      {!connected ? (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
              {needsConfiguration && isAdmin
                ? 'Configure Dropbox app credentials to enable file synchronization.'
                : 'Connect your Dropbox account to enable file synchronization.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3, bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
            <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
              <strong>How Content Updates Work:</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              • Files from Dropbox are automatically imported into your Library as assets<br />
              • When files are updated in Dropbox, they are automatically updated in your Library (if auto-overwrite is enabled)<br />
              • The system tracks file changes using content hashes, so each sync only downloads new or changed files<br />
              • Each sync always reads from the Dropbox account it was created with, for scheduled and manual runs alike<br />
              • You can schedule automatic syncs (hourly, daily, custom times) or sync manually
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#22d3ee' }}>
              Sync Configurations
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenSyncDialog()}
              sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
            >
              New Sync
            </Button>
          </Box>

          {syncs.length === 0 ? (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No sync configurations. Create one to start syncing files from Dropbox.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#22d3ee' }}>Name</TableCell>
                    <TableCell sx={{ color: '#22d3ee' }}>Station</TableCell>
                    <TableCell sx={{ color: '#22d3ee' }}>Dropbox Path</TableCell>
                    <TableCell sx={{ color: '#22d3ee' }}>Schedule</TableCell>
                    <TableCell sx={{ color: '#22d3ee' }}>Status</TableCell>
                    <TableCell sx={{ color: '#22d3ee' }}>Last Sync</TableCell>
                    <TableCell sx={{ color: '#22d3ee' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncs.map((sync) => (
                    <TableRow key={sync.id}>
                      <TableCell>{sync.name}</TableCell>
                      <TableCell>
                        {stations.find((s) => s.id === sync.station_id)?.name || sync.station_id}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {sync.dropbox_path}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(sync.sync_schedule || sync.sync_interval_minutes) ? (
                          <Chip
                            icon={<Schedule />}
                            label={formatCronExpression(sync)}
                            size="small"
                            sx={{ bgcolor: '#22d3ee20', color: '#22d3ee' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Manual
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sync.enabled ? 'Enabled' : 'Disabled'}
                          size="small"
                          color={sync.enabled ? 'success' : 'default'}
                        />
                        {sync.last_sync_status && (
                          <Chip
                            label={sync.last_sync_status}
                            size="small"
                            color={getStatusColor(sync.last_sync_status)}
                            sx={{ ml: 1 }}
                          />
                        )}
                        {sync.last_sync_status === 'error' && sync.last_sync_error && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', mt: 0.5, maxWidth: 280 }}
                          >
                            {sync.last_sync_error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {sync.last_sync_at
                          ? new Date(sync.last_sync_at).toLocaleString()
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleSyncNow(sync.id)}
                          disabled={loading}
                          sx={{ color: '#22d3ee' }}
                          title="Sync Now"
                        >
                          <Sync />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleViewLogs(sync.id)}
                          sx={{ color: '#22d3ee' }}
                          title="View Logs"
                        >
                          <Folder />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSyncDialog(sync)}
                          sx={{ color: '#22d3ee' }}
                          title="Edit"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSync(sync.id)}
                          sx={{ color: '#ff6b6b' }}
                          title="Delete"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Sync Configuration Dialog */}
          <Dialog open={syncDialogOpen} onClose={handleCloseSyncDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              <Typography variant="h6">
                {editingSync ? 'Edit Sync Configuration' : 'New Sync Configuration'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name *"
                    value={syncFormData.name}
                    onChange={(e) => setSyncFormData({ ...syncFormData, name: e.target.value })}
                    placeholder="e.g., Morning News Updates"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Station *</InputLabel>
                    <Select
                      value={syncFormData.station_id}
                      label="Station *"
                      onChange={(e) => setSyncFormData({ ...syncFormData, station_id: e.target.value, local_category_id: '' })}
                    >
                      {stations.map((station) => (
                        <MenuItem key={station.id} value={station.id}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category (Optional)</InputLabel>
                    <Select
                      value={syncFormData.local_category_id}
                      label="Category (Optional)"
                      onChange={(e) => setSyncFormData({ ...syncFormData, local_category_id: e.target.value })}
                    >
                      <MenuItem value="">None</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#22d3ee' }}>
                      Select Dropbox Folder
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => loadDropboxFiles('')}
                        disabled={loadingFiles}
                      >
                        Browse Dropbox
                      </Button>
                      {dropboxPath && (
                        <Chip
                          label={`Selected: ${dropboxPath}`}
                          onDelete={() => {
                            setDropboxPath('');
                            setSyncFormData({ ...syncFormData, dropbox_path: '' });
                          }}
                          color="primary"
                        />
                      )}
                    </Box>
                    {currentPath && (
                      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => {
                            const parent = currentPath.split('/').slice(0, -1).join('/') || '';
                            loadDropboxFiles(parent);
                          }}
                          sx={{ color: '#22d3ee' }}
                        >
                          Root
                        </Link>
                        {currentPath.split('/').filter(Boolean).map((part, index, arr) => {
                          const path = '/' + arr.slice(0, index + 1).join('/');
                          return (
                            <Link
                              key={path}
                              component="button"
                              variant="body2"
                              onClick={() => loadDropboxFiles(path)}
                              sx={{ color: '#22d3ee' }}
                            >
                              {part}
                            </Link>
                          );
                        })}
                      </Breadcrumbs>
                    )}
                    {loadingFiles ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : dropboxFiles.length > 0 ? (
                      <Paper sx={{ bgcolor: 'var(--rs-bg)', maxHeight: 300, overflow: 'auto' }}>
                        <List>
                          {dropboxFiles.map((file) => (
                            <ListItem key={file.path} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  if (file.isFolder) {
                                    loadDropboxFiles(file.path);
                                  } else {
                                    handleSelectFolder(file.path);
                                  }
                                }}
                              >
                                <ListItemIcon>
                                  {file.isFolder ? (
                                    <Folder sx={{ color: '#22d3ee' }} />
                                  ) : (
                                    <InsertDriveFile sx={{ color: '#999' }} />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={file.name}
                                  secondary={file.isFolder ? 'Folder' : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    ) : currentPath ? (
                      <Alert severity="info">No files or folders found</Alert>
                    ) : null}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dropbox Path *"
                    value={syncFormData.dropbox_path}
                    onChange={(e) => setSyncFormData({ ...syncFormData, dropbox_path: e.target.value })}
                    placeholder="/News Updates"
                    helperText="Path to the Dropbox folder to sync"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#22d3ee' }}>
                    Sync Schedule
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Schedule Type</InputLabel>
                    <Select
                      value={scheduleType}
                      label="Schedule Type"
                      onChange={(e) => {
                        setScheduleType(e.target.value);
                        if (e.target.value === 'manual') {
                          setSyncFormData({ ...syncFormData, sync_schedule: '', sync_interval_minutes: '' });
                        }
                      }}
                    >
                      <MenuItem value="manual">Manual Only</MenuItem>
                      <MenuItem value="interval">Every X Minutes/Hours</MenuItem>
                      <MenuItem value="cron">Custom Cron Expression</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {scheduleType === 'interval' && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Sync Interval (minutes)"
                        type="number"
                        value={syncFormData.sync_interval_minutes}
                        onChange={(e) => setSyncFormData({ ...syncFormData, sync_interval_minutes: e.target.value, sync_schedule: '' })}
                        placeholder="60"
                        helperText="Sync every X minutes (e.g., 60 = hourly, 30 = every 30 minutes, 1440 = daily)"
                        inputProps={{ min: 1 }}
                      />
                      {syncFormData.sync_interval_minutes && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'var(--rs-bg)', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {parseInt(syncFormData.sync_interval_minutes) >= 60 
                              ? `Every ${Math.floor(parseInt(syncFormData.sync_interval_minutes) / 60)} hour(s)`
                              : `Every ${syncFormData.sync_interval_minutes} minute(s)`
                            }
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {scheduleType === 'cron' && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Cron Expression"
                        value={syncFormData.sync_schedule}
                        onChange={(e) => setSyncFormData({ ...syncFormData, sync_schedule: e.target.value, sync_interval_minutes: '' })}
                        placeholder="0 8,12,16 * * *"
                        helperText="Cron expression for scheduled syncs"
                      />
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'var(--rs-bg)', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
                          Examples:
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ccc', display: 'block', fontFamily: 'monospace' }}>
                          • 0 * * * * - Every hour<br />
                          • 0 8,12,16 * * * - 8am, 12pm, 4pm daily<br />
                          • */30 * * * * - Every 30 minutes<br />
                          • 0 0 * * * - Daily at midnight
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {scheduleType === 'manual' && (
                    <Alert severity="info" sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                      <Typography variant="body2" sx={{ color: '#ccc' }}>
                        Files will only sync when you manually trigger a sync. Use the sync button (🔄) next to each sync configuration to sync on demand.
                      </Typography>
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ display: 'block', color: '#999' }}>
                    Every sync only downloads files that are new or whose content changed in Dropbox since the last run; unchanged files are skipped.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncFormData.auto_import}
                        onChange={(e) => setSyncFormData({ ...syncFormData, auto_import: e.target.checked })}
                      />
                    }
                    label="Auto Import"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncFormData.auto_overwrite}
                        onChange={(e) => setSyncFormData({ ...syncFormData, auto_overwrite: e.target.checked })}
                      />
                    }
                    label="Auto Overwrite"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncFormData.enabled}
                        onChange={(e) => setSyncFormData({ ...syncFormData, enabled: e.target.checked })}
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSyncDialog}>Cancel</Button>
              <Button
                onClick={handleSaveSync}
                variant="contained"
                disabled={loading}
                sx={{ bgcolor: '#22d3ee' }}
              >
                {editingSync ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Sync Logs Dialog */}
          <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Sync Logs</DialogTitle>
            <DialogContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No logs yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      syncLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {log.file_path}
                            </Typography>
                          </TableCell>
                          <TableCell>{log.action}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.status}
                              size="small"
                              color={log.status === 'success' ? 'success' : 'error'}
                              icon={log.status === 'success' ? <CheckCircle /> : <ErrorIcon />}
                            />
                          </TableCell>
                          <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Configuration Dialog (Admin Only) */}
      {isAdmin && (
        <Dialog 
          open={configDialogOpen} 
          onClose={() => setConfigDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'var(--rs-paper)',
              border: '1px solid var(--rs-border)',
            }
          }}
        >
          <DialogTitle sx={{ color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload sx={{ color: '#22d3ee' }} />
            Configure Dropbox App Credentials
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Enter your Dropbox App credentials. These are obtained from the{' '}
                  <Link 
                    href="https://www.dropbox.com/developers/apps" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: '#22d3ee', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    Dropbox App Console
                    <OpenInNew sx={{ fontSize: 14 }} />
                  </Link>
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dropbox App Key *"
                    value={dropboxConfig.appKey}
                    onChange={(e) => setDropboxConfig({ ...dropboxConfig, appKey: e.target.value })}
                    placeholder="Enter your Dropbox App Key"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dropbox App Secret *"
                    type="password"
                    value={dropboxConfig.appSecret}
                    onChange={(e) => setDropboxConfig({ ...dropboxConfig, appSecret: e.target.value })}
                    placeholder="Enter your Dropbox App Secret"
                    required
                    helperText="Your App Secret will be encrypted and stored securely"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Redirect URI"
                    value={dropboxConfig.redirectUri}
                    onChange={(e) => setDropboxConfig({ ...dropboxConfig, redirectUri: e.target.value })}
                    helperText="This should match the redirect URI configured in your Dropbox app"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'var(--rs-paper)', borderTop: '1px solid var(--rs-border)' }}>
            <Button 
              onClick={() => setConfigDialogOpen(false)}
              sx={{ color: '#999' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveConfig}
              disabled={configLoading || !dropboxConfig.appKey || !dropboxConfig.appSecret}
              sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
            >
              Save Configuration
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Instructions Dialog */}
      <Dialog 
        open={instructionsDialogOpen} 
        onClose={() => setInstructionsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--rs-paper)',
            border: '1px solid var(--rs-border)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info sx={{ color: '#22d3ee' }} />
          How to Connect Dropbox
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, color: 'var(--rs-text)' }}>
              Follow these steps to connect your Dropbox account to RadioSuiteX:
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
                Step 1: Create a Dropbox App
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
                1. Go to{' '}
                <Link 
                  href="https://www.dropbox.com/developers/apps" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ color: '#22d3ee', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  Dropbox App Console
                  <OpenInNew sx={{ fontSize: 14 }} />
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                2. Click "Create app"
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                3. Choose the following settings:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2, color: '#ccc' }}>
                <li><Typography variant="body2" component="span">API: <strong>Scoped access</strong></Typography></li>
                <li><Typography variant="body2" component="span">Type of data access: <strong>Full Dropbox</strong></Typography></li>
                <li><Typography variant="body2" component="span">Name: RadioSuiteX (or your preferred name)</Typography></li>
              </Box>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                4. Click "Create app"
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
                Step 2: Configure Permissions
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                1. In your Dropbox App Console, go to the <strong>"Permissions"</strong> tab
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                2. Enable the following scopes, then click <strong>"Submit"</strong>:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2, color: '#ccc' }}>
                <li><Typography variant="body2" component="span"><code>files.metadata.read</code></Typography></li>
                <li><Typography variant="body2" component="span"><code>files.content.read</code></Typography></li>
                <li><Typography variant="body2" component="span"><code>files.content.write</code></Typography></li>
                <li><Typography variant="body2" component="span"><code>account_info.read</code> (optional, for displaying account info)</Typography></li>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
                Step 3: Configure Redirect URI
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                1. Go to the <strong>"Settings"</strong> tab in the Dropbox App Console
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                2. Under "OAuth 2", add your redirect URI:
              </Typography>
              <Box sx={{ bgcolor: 'var(--rs-bg)', p: 2, borderRadius: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.85rem', color: '#22d3ee' }}>
                {API_URL}/api/dropbox/auth/callback
              </Box>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                3. Note your <strong>App key</strong> and <strong>App secret</strong> from the app settings
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
                Step 4: Configure Dropbox Credentials
              </Typography>
              {isAdmin ? (
                <>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
                    As an administrator, you can configure Dropbox credentials directly in this interface:
                  </Typography>
                  <Box sx={{ bgcolor: 'var(--rs-bg)', p: 2, borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                      1. Click the "Configure Dropbox Credentials" button (if shown) or use the settings icon
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                      2. Enter your Dropbox App Key and App Secret from Step 3
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      3. The Redirect URI will be automatically set, but you can customize it if needed
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => {
                      setInstructionsDialogOpen(false);
                      setConfigDialogOpen(true);
                    }}
                    sx={{ borderColor: '#22d3ee', color: '#22d3ee', '&:hover': { borderColor: '#00b8e6' } }}
                  >
                    Open Configuration Dialog
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
                    Contact your administrator to configure Dropbox app credentials. They need to:
                  </Typography>
                  <Box sx={{ bgcolor: 'var(--rs-bg)', p: 2, borderRadius: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.85rem', color: '#ccc' }}>
                    • Enter Dropbox App Key<br />
                    • Enter Dropbox App Secret<br />
                    • Configure Redirect URI: {API_URL}/api/dropbox/auth/callback
                  </Box>
                  <Alert severity="info" sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)', color: '#ccc' }}>
                    <Typography variant="body2">
                      Administrators can configure these credentials directly in the Settings → Dropbox section.
                    </Typography>
                  </Alert>
                </>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
                Step 5: Connect Your Account
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                1. Click the "Connect Dropbox" button above
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                2. You'll be redirected to Dropbox to authorize the app
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                3. After authorization, you'll be redirected back to RadioSuiteX
              </Typography>
            </Box>

            <Alert severity="success" sx={{ bgcolor: 'var(--rs-bg)', border: '1px solid #22d3ee', mt: 3 }}>
              <Typography variant="body2" sx={{ color: '#22d3ee' }}>
                <strong>Once connected, you can:</strong>
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, color: '#ccc' }}>
                <li><Typography variant="body2" component="span">Create sync configurations to automatically import files from Dropbox folders</Typography></li>
                <li><Typography variant="body2" component="span">Schedule automatic syncs at specific times (e.g., news updates)</Typography></li>
                <li><Typography variant="body2" component="span">Manually trigger syncs whenever needed</Typography></li>
                <li><Typography variant="body2" component="span">View detailed sync logs and history</Typography></li>
              </Box>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'var(--rs-paper)', borderTop: '1px solid var(--rs-border)' }}>
          <Button 
            onClick={() => setInstructionsDialogOpen(false)}
            sx={{ color: '#22d3ee' }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => {
              setInstructionsDialogOpen(false);
              handleConnect();
            }}
            sx={{ bgcolor: '#0061ff', '&:hover': { bgcolor: '#0052cc' } }}
          >
            Connect Dropbox Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
